import React from 'react';
import { connect } from 'react-redux';
import { fetchMachineStatus, fetchUsersOnline } from '../actions/StatusActions';
import { getProductConfig } from '../backend';
import CamButtonsView from './CamButtonsView';
import CameraOverlaysMenu from './CameraOverlaysMenu';
import LayoutMenu from './LayoutMenu';
import SystemInfoPopover from './SystemInfoPopover';

/**
 * DesktopBottomBar — the desktop Live bottom bar (redesign #86bb5dx62, Mock D4).
 *   LEFT   — Scan (labeled, mobile/tablet style, active state) · Fullscreen · Overlays gear ⚙
 *   CENTER — camera banks (CamButtonsView), absolutely centered on the VIDEO container
 *            (offset by half the rail width) so they land on the 4-up divider
 *   RIGHT  — status (● N Online · storage · N Days Rec) · (i) system-details popover
 *
 * Reuses CamButtonsView (banks) with hideAutoScan (the left Scan button owns auto-scan),
 * and the StatusActions data layer (fetchMachineStatus / fetchUsersOnline) that StatusView
 * uses — StatusView itself is left untouched (MobileMoreMenu reuses it), so this bar owns
 * the desktop status polling directly. Desktop-only via CSS; never affects mobile/tablet.
 *
 * Props: autoScanning, toggleAutoScan, onFullscreen, onSnapshotAll, onResetZoom
 */
class DesktopBottomBar extends React.Component {
    componentDidMount() {
        if (!getProductConfig().pollsSystemStatus) return;
        this.fetchStatus();
        this._pollTimer = setInterval(() => this.fetchStatus(), 60000);
    }
    componentWillUnmount() {
        if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
    }
    componentDidUpdate(prevProps) {
        if (prevProps.sSess !== this.props.sSess && this.props.sSess) this.fetchStatus();
    }
    fetchStatus() {
        const { sSess, dispatch } = this.props;
        if (!sSess || !getProductConfig().pollsSystemStatus) return;
        dispatch(fetchMachineStatus(sSess));
        dispatch(fetchUsersOnline(sSess));
    }

    render() {
        const { machineStatus, cameras, bSerial, conf, onSelectLayout,
                autoScanning, toggleAutoScan, onFullscreen, onSnapshotAll, onResetZoom } = this.props;

        // The bottom bar now shows ONLY "N Days Rec" (David) — Online + storage moved to the (i)
        // popover. Hide days on a cam-less (eview) controller where "0 Days" would mislead.
        const daysLoaded = machineStatus && typeof machineStatus.days === 'number';
        const hasEnabledCams = Array.isArray(cameras) && cameras.some((c) =>
            c && c.fEnable === true && (c.bSerial === undefined || c.bSerial === bSerial));
        const days = daysLoaded ? machineStatus.days : 0;

        return (
            <div className="live-dbot">
                <div className="live-dbot__left">
                    <button
                        className={'live-bscan' + (autoScanning ? ' live-bscan--on' : '')}
                        onClick={toggleAutoScan}
                        title="Auto Scan"
                        aria-pressed={!!autoScanning}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" /></svg>
                        <span>Scan</span>
                    </button>
                    <button className="live-bfs" onClick={onFullscreen} title="Fullscreen">
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
                    </button>
                    {/* Overlays gear, then Layout last — Layout is the most-used of this left
                        group, so it sits rightmost (closest to the banks/video), David #86bb5dx62. */}
                    <CameraOverlaysMenu onSnapshotAll={onSnapshotAll} onResetZoom={onResetZoom} />
                    <LayoutMenu conf={conf} onSelect={onSelectLayout} />
                </div>

                {/* Banks — centered on the video container (the 4-up divider) via CSS offset. */}
                <div className="live-dbot__banks">
                    <CamButtonsView hideAutoScan autoScanning={autoScanning} toggleAutoScan={toggleAutoScan} />
                </div>

                <div className="live-dbot__right">
                    <div className="live-statusbar">
                        {daysLoaded && hasEnabledCams && (
                            <span className="live-statusbar__stat" title="Days of recorded footage">
                                <b>{days}</b> {days === 1 ? 'Day Rec' : 'Days Rec'}
                            </span>
                        )}
                    </div>
                    <SystemInfoPopover />
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    sSess: state.auth.sSess,
    usersOnline: state.server.usersOnline,
    machineStatus: state.server.machineStatus,
    cameras: state.server.cameras,
    bSerial: state.server.bSerial
});

export default connect(mapStateToProps)(DesktopBottomBar);
